import { Component } from '@theme/component';

/** Independent infinite variant of the merchant's published social carousel. */
class YumiSwipeCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-ysi-track]');
    this.viewport = this.querySelector('.ysi-viewport');
    if (!this.track || !this.viewport) return;
    this.slides = [...this.track.children].filter(card => !card.hasAttribute('data-ysi-clone'));
    this.rtl = getComputedStyle(this.track).direction === 'rtl';
    this.dots = this.querySelector('[data-ysi-dots]');
    this.cursor = this.querySelector('[data-ysi-cursor]');
    this.activeIndex = 0;
    this.dots?.replaceChildren(...this.slides.map((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ysi-dot';
      button.setAttribute('aria-label', (this.dots.dataset.label || '').replace('[number]', String(index + 1)));
      button.addEventListener('click', () => this.go(index), { signal });
      return button;
    }));
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.normalize(), 160);
    }, { passive: true, signal });
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.go(0);
      else if (event.key === 'End') this.go(this.slides.length - 1);
      else this.move((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
    }, { signal });
    this.querySelector('[data-ysi-prev]')?.addEventListener('click', () => this.move(-1), { signal });
    this.querySelector('[data-ysi-next]')?.addEventListener('click', () => this.move(1), { signal });
    this.track.addEventListener('dragstart', event => event.preventDefault(), { signal });
    this.track.addEventListener('pointerdown', event => this.onPointerDown(event), { signal });
    this.track.addEventListener('pointermove', event => this.onPointerMove(event), { signal });
    this.track.addEventListener('pointerup', event => this.onPointerUp(event), { signal });
    this.track.addEventListener('pointercancel', event => this.onPointerUp(event), { signal });
    this.track.addEventListener('click', event => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.suppressClick = false;
    }, { capture: true, signal });
    this.viewport.addEventListener('pointermove', event => this.showCursor(event), { signal });
    this.viewport.addEventListener('pointerleave', () => {
      if (this.cursor) this.cursor.hidden = true;
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.track.querySelectorAll('video').forEach(video => video.pause());
    }, { signal });
    document.addEventListener('shopify:block:select', event => {
      const index = this.slides.findIndex(slide => {
        try { return JSON.parse(slide.dataset.shopifyEditorBlock || '{}').id === event.detail?.blockId; }
        catch { return false; }
      });
      if (index !== -1) this.go(index, true);
    }, { signal });
    this.videoObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const video = entry.target;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) video.pause();
        else if (video.dataset.ysiAutoplay === 'true' && !this.reducedMotion()) video.play().catch(() => {});
      }
    }, { threshold: [0, 0.5] });
    this.build();
    this.resizeObserver = new ResizeObserver(() => {
      if (Math.abs(this.track.clientWidth - this.lastWidth) > 1) this.build();
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.videoObserver?.disconnect();
    clearTimeout(this.settleTimer);
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.snapFrame);
    cancelAnimationFrame(this.dragFrame);
    this.cancelMotion();
    this.track?.querySelectorAll('video').forEach(video => video.pause());
    this.track?.querySelectorAll('[data-ysi-clone]').forEach(clone => clone.remove());
    this.track?.style.removeProperty('scroll-snap-type');
    this.drag = null;
  }

  reducedMotion() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }
  desktop() { return matchMedia('(min-width: 750px)').matches; }
  position() { return Math.abs(this.track.scrollLeft); }
  setPosition(value, smooth = false, release = false) {
    if (smooth && this.desktop() && !this.reducedMotion()) {
      this.animateTo(value, release);
      return;
    }
    this.track.scrollTo({ left: (this.rtl ? -1 : 1) * value, behavior: smooth && !this.reducedMotion() ? 'smooth' : 'instant' });
  }
  cancelMotion() {
    cancelAnimationFrame(this.motionFrame);
    this.motionFrame = null;
  }

  animateTo(value, release) {
    this.cancelMotion();
    clearTimeout(this.settleTimer);
    const from = this.position();
    const start = performance.now();
    // Explicit easing avoids the browser's desktop snap interrupting the release.
    const duration = 420;
    const tick = now => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = release ? 1 - Math.pow(1 - progress, 3)
        : progress < 0.5 ? 4 * progress ** 3 : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      this.setPosition(from + (value - from) * eased);
      if (progress < 1) this.motionFrame = requestAnimationFrame(tick);
      else {
        this.motionFrame = null;
        this.normalize();
      }
    };
    this.motionFrame = requestAnimationFrame(tick);
  }
  instant(value) {
    cancelAnimationFrame(this.snapFrame);
    this.track.style.scrollSnapType = 'none';
    this.setPosition(value);
    this.snapFrame = requestAnimationFrame(() => {
      if (!this.drag?.moved) this.track.style.removeProperty('scroll-snap-type');
    });
  }

  build() {
    this.cancelMotion();
    cancelAnimationFrame(this.dragFrame);
    this.drag = null;
    const index = this.activeIndex || 0;
    this.videoObserver?.disconnect();
    this.track.querySelectorAll('[data-ysi-clone]').forEach(clone => clone.remove());
    this.lastWidth = this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.step = (this.slides[0]?.getBoundingClientRect().width || 0) + gap;
    this.cycle = this.step * this.slides.length;
    this.prefixCount = this.slides.length > 1 && this.cycle > 0
      ? this.slides.length * Math.max(1, Math.ceil(this.lastWidth / this.cycle)) : 0;
    if (this.prefixCount) {
      const before = document.createDocumentFragment(), after = document.createDocumentFragment();
      for (let i = 0; i < this.prefixCount; i++) {
        before.append(this.clone(this.slides[i % this.slides.length]));
        after.append(this.clone(this.slides[i % this.slides.length]));
      }
      this.track.prepend(before);
      this.track.append(after);
    }
    this.origin = this.prefixCount * this.step;
    this.instant(this.origin + index * this.step);
    this.track.querySelectorAll('video').forEach(video => this.videoObserver?.observe(video));
    for (const name of ['prev', 'next']) {
      const button = this.querySelector(`[data-ysi-${name}]`);
      if (button) button.disabled = this.slides.length < 2;
    }
    this.dataset.ready = 'true';
    this.update();
  }

  clone(slide) {
    const clone = slide.cloneNode(true);
    clone.dataset.ysiClone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-shopify-editor-block');
    clone.removeAttribute('role');
    for (const node of [clone, ...clone.querySelectorAll('*')]) {
      node.removeAttribute('id');
      node.removeAttribute('data-shopify-editor-block');
      if (node.matches('a,button,input,select,textarea,video,[tabindex]')) node.setAttribute('tabindex', '-1');
      if (node instanceof HTMLVideoElement) node.autoplay = false;
    }
    return clone;
  }

  update() {
    if (!this.step || !this.slides.length) return;
    const raw = Math.round((this.position() - this.origin) / this.step);
    this.activeIndex = ((raw % this.slides.length) + this.slides.length) % this.slides.length;
    this.dataset.activeIndex = String(this.activeIndex);
    [...(this.dots?.children || [])].forEach((button, index) => {
      if (index === this.activeIndex) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }

  normalize() {
    if (this.drag || this.motionFrame || !this.cycle || !this.prefixCount) return;
    const offset = this.position() - this.origin;
    if (offset < -1 || offset >= this.cycle - 1) {
      const within = ((offset % this.cycle) + this.cycle) % this.cycle;
      this.instant(this.origin + within);
    }
    this.update();
  }

  move(direction) {
    if (this.slides.length < 2) return;
    this.cancelMotion();
    this.normalize();
    let raw = Math.round((this.position() - this.origin) / this.step);
    if (raw + direction > this.slides.length || raw + direction < -1) {
      const shift = raw > 0 ? -this.cycle : this.cycle;
      this.instant(this.position() + shift);
      raw += shift / this.step;
    }
    this.setPosition(this.origin + (raw + direction) * this.step, true);
  }

  go(index, instant = false) {
    if (!this.step || !this.slides.length) return;
    this.cancelMotion();
    this.normalize();
    const target = this.origin + index * this.step;
    if (instant) this.instant(target);
    else this.setPosition(target, true);
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('video,button,input')) return;
    this.cancelMotion();
    this.normalize();
    this.suppressClick = false;
    this.drag = { x: event.clientX, position: this.position(), target: this.position(), pointerId: event.pointerId, moved: false, lastX: event.clientX, lastTime: performance.now(), velocity: 0 };
  }

  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const delta = event.clientX - this.drag.x;
    if (!this.drag.moved && Math.abs(delta) < 6) return;
    this.drag.moved = true;
    this.suppressClick = true;
    this.track.setPointerCapture(event.pointerId);
    this.track.style.scrollSnapType = 'none';
    this.dataset.dragging = 'true';
    event.preventDefault();
    const now = performance.now();
    const elapsed = Math.max(1, now - this.drag.lastTime);
    const speed = -(event.clientX - this.drag.lastX) * (this.rtl ? -1 : 1) / elapsed;
    this.drag.velocity = this.drag.velocity * 0.6 + speed * 0.4;
    this.drag.lastTime = now;
    this.drag.lastX = event.clientX;
    this.drag.target = this.drag.position - delta * (this.rtl ? -1 : 1);
    if (!this.dragFrame) this.dragFrame = requestAnimationFrame(time => this.renderDrag(time));
  }

  renderDrag(time) {
    this.dragFrame = null;
    if (!this.drag?.moved) return;
    const elapsed = this.drag.frameTime ? Math.min(40, time - this.drag.frameTime) : 16;
    this.drag.frameTime = time;
    const follow = this.reducedMotion() ? 1 : 1 - Math.exp(-elapsed / 22);
    this.setPosition(this.position() + (this.drag.target - this.position()) * follow);
    // Recenter during a long drag without changing the visible card order.
    const offset = this.position() - this.origin;
    if (this.cycle && (offset < -this.cycle / 2 || offset > this.cycle * 1.5)) {
      const shift = offset < 0 ? this.cycle : -this.cycle;
      this.setPosition(this.position() + shift);
      this.drag.position += shift;
      this.drag.target += shift;
    }
    if (Math.abs(this.drag.target - this.position()) > 0.25) this.dragFrame = requestAnimationFrame(next => this.renderDrag(next));
  }

  onPointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const moved = this.drag.moved;
    const velocity = this.drag.velocity * Math.exp(-(performance.now() - this.drag.lastTime) / 80);
    const momentum = this.desktop() && !this.reducedMotion() && event.type !== 'pointercancel'
      ? Math.max(-this.step * 0.75, Math.min(this.step * 0.75, velocity * 100)) : 0;
    const target = this.drag.target + momentum;
    cancelAnimationFrame(this.dragFrame);
    this.dragFrame = null;
    if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
    this.drag = null;
    delete this.dataset.dragging;
    this.track.style.removeProperty('scroll-snap-type');
    if (moved && this.step) this.setPosition(Math.round(target / this.step) * this.step, true, true);
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.normalize(), 180);
  }

  showCursor(event) {
    if (!this.cursor || event.pointerType !== 'mouse' || !matchMedia('(min-width: 750px) and (hover: hover)').matches) return;
    this.cursor.hidden = !!event.target.closest('button,video');
    const rect = this.viewport.getBoundingClientRect();
    this.cursor.style.left = `${event.clientX - rect.left}px`;
    this.cursor.style.top = `${event.clientY - rect.top}px`;
  }
}

if (!customElements.get('yumi-swipe-carousel')) customElements.define('yumi-swipe-carousel', YumiSwipeCarousel);
